import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { useNotifications } from "@/contexts/NotificationContext";
import TemplateDeleteDialog from "./components/TemplateDeleteDialog";
import { EditorMode } from "./types";

// Import unified hook
import { useTemplateState } from "./hooks";

// Import components
import { TemplateForm, TemplateHeader } from "./components";

interface TemplateEditorProps {
  mode?: EditorMode;
  templateId?: string;
  baseTemplateId?: string;
}

const TemplateEditor = ({ mode = "create", templateId, baseTemplateId }: TemplateEditorProps) => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  // Get actual mode
  const actualMode = templateId ? "edit" : mode;

  // Use unified hook for all state management
  const {
    formData,
    template,
    baseTemplate,
    permissions,
    nameEditorState,
    isLoading,
    error,
    isMobile,
    showAdvancedSettings,
    deleteDialogOpen,
    isPromptTouched,
    isAiInstructionDirty,
    isUnsaved,
    dataLoadedKey,
    saveMutation,
    deleteTemplateMutation,
    duplicateTemplateMutation,
    actions,
    updateField,
    setShowAdvancedSettings,
    setDeleteDialogOpen,
    user,
  } = useTemplateState(mode, templateId, baseTemplateId);


  // Unified render for all modes
  return (
    <MainLayout>
      <div className="container-page flex flex-col min-h-[calc(100vh-theme(spacing.16))] md:min-h-auto md:block">
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded mb-4"></div>
            <div className="h-12 bg-muted rounded mb-4"></div>
            <div className="h-24 bg-muted rounded"></div>
          </div>
        ) : (
          <div className="max-w-2xl md:max-w-4xl xl:max-w-5xl flex-1 flex flex-col md:flex-none md:block pb-24 md:pb-0">
            {/* Template Header */}
            <div className="mb-6">
              <TemplateHeader
                template={template}
                permissions={permissions}
                actions={actions}
                nameEditorState={nameEditorState}
                formData={formData}
                baseTemplateId={baseTemplateId}
                baseTemplate={baseTemplate}
                isSaving={saveMutation.isPending}
                isDeleting={deleteTemplateMutation.isPending}
                isUnsaved={isUnsaved}
                mode={actualMode}
              />
            </div>

            <TemplateForm
              formData={formData}
              onUpdateField={updateField}
              permissions={permissions}
              user={user}
              baseTemplateId={baseTemplateId}
              baseTemplate={baseTemplate}
              showAdvanced={showAdvancedSettings}
              onShowAdvancedChange={setShowAdvancedSettings}
              isPromptTouched={isPromptTouched}
              isAiInstructionDirty={isAiInstructionDirty}
              isMobile={isMobile}
              dataLoadedKey={dataLoadedKey}
            />

            {/* Delete confirmation dialog */}
            {template && (
              <TemplateDeleteDialog
                isOpen={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                templateName={template.name}
                onConfirm={() => deleteTemplateMutation.mutate(template.id)}
                isDeleting={deleteTemplateMutation.isPending}
              />
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default TemplateEditor;
